/**
 * Clave.tsx — poner una contraseña: al llegar invitado, o al recuperarla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ ES UNA PANTALLA PARA DOS CORREOS, Y ESO NO ES AHORRO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La invitación (E8.1) y la recuperación mandan el MISMO enlace con distinto
 * `type`, y en los dos casos la persona llega a hacer exactamente lo mismo:
 * escribir una contraseña por primera vez. Dos pantallas serían dos sitios
 * donde arreglar el día que Supabase cambie la forma del enlace.
 *
 * Y hasta hoy no había ninguna: E8.1 automatizó invitar y **nadie recibía al
 * invitado**. El correo llegaba, el enlace abría la aplicación, la aplicación
 * no miraba la URL y la persona veía un formulario de acceso pidiéndole una
 * contraseña que nunca había puesto.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LA URL SE BORRA EN CUANTO SE LEE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El enlace trae una credencial. `replaceState` la saca de la barra y de la
 * entrada del historial antes de que la persona toque nada — si no, queda ahí
 * hasta que cierre la pestaña, y una recarga la volvería a canjear.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y AQUÍ NO SE DICE SI EL CORREO EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Al pedir la recuperación, la respuesta es la misma exista o no la cuenta:
 * «si esa dirección está en Books, va a llegar un correo». Confirmarlo
 * convertiría esta pantalla en una forma de averiguar quién es cliente
 * nuestro, escribiendo direcciones y mirando cuál cambia el mensaje.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { supabase, configurado } from '../nucleo/supabase.ts';
import { leerLlegada, revisarClave } from '../nucleo/recuperacion.ts';
import { Isotipo } from '../componentes/Iconos.tsx';
import type { Clave as ClaveDeTexto } from '../nucleo/idioma.ts';

type Paso =
  | { que: 'canjeando' }
  | { que: 'formulario'; invitado: boolean }
  | { que: 'sin_enlace' }
  | { que: 'fallo'; motivo: string }
  | { que: 'listo' };

/** El mismo marco oscuro del acceso. Llegar por correo no es entrar a otro producto. */
function Marco({ children }: { children: React.ReactNode }) {
  const { t, idioma, cambiar } = useIdioma();
  return (
    <div className="acceso">
      <div className="bgwrap" aria-hidden="true" />
      <div className="shell">
        <div className="win">
          <div className="console">
            <div className="console__bar">
              <span className="console__dots" aria-hidden="true"><i /><i /><i /></span>
              <span className="console__path">skoped-books://</span>
            </div>
            <div className="console__body">
              <Isotipo className="iso-glow" tono="oscuro" />
              {/* `h1`: ver el comentario en Acceso.tsx. */}
              <h1 className="title wordmark wordmark--dark" style={{ fontSize: 21, margin: 0 }}>
                Skoped <span>Books</span>
              </h1>
              {children}
            </div>
          </div>
          <div className="langpick" role="group" aria-label="Idioma / Language">
            <button type="button" aria-pressed={idioma === 'es'} onClick={() => cambiar('es')}>
              ESPAÑOL
            </button>
            <button type="button" aria-pressed={idioma === 'en'} onClick={() => cambiar('en')}>
              ENGLISH
            </button>
          </div>
          <div className="brandline">
            <span className="dot">◆</span> {t('login.byLabs')}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Clave() {
  const { t } = useIdioma();
  const [paso, setPaso] = useState<Paso>({ que: 'canjeando' });
  const [clave, setClave] = useState('');
  const [repetida, setRepetida] = useState('');
  const [aviso, setAviso] = useState<ClaveDeTexto | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!configurado || !supabase) { setPaso({ que: 'fallo', motivo: 'sin_configurar' }); return; }

    const llegada = leerLlegada(window.location.href);

    // ▶ Fuera de la URL antes de nada. Ver la cabecera.
    if (llegada.que !== 'nada') {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (llegada.que === 'nada') { setPaso({ que: 'sin_enlace' }); return; }
    if (llegada.que === 'fallo') {
      setPaso({ que: 'fallo', motivo: `enlace_${llegada.motivo}` });
      return;
    }

    const invitado = llegada.tipo === 'invite' || llegada.tipo === 'signup';

    void (async () => {
      try {
        if (llegada.que === 'vale') {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: llegada.tokenHash,
            type: llegada.tipo === 'magiclink' ? 'magiclink' : llegada.tipo,
          });
          if (error) { setPaso({ que: 'fallo', motivo: 'enlace_vencido' }); return; }
        } else {
          const { error } = await supabase.auth.setSession({
            access_token: llegada.accessToken,
            refresh_token: llegada.refreshToken,
          });
          if (error) { setPaso({ que: 'fallo', motivo: 'enlace_vencido' }); return; }
        }
        setPaso({ que: 'formulario', invitado });
      } catch {
        setPaso({ que: 'fallo', motivo: 'sin_conexion' });
      }
    })();
  }, []);

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const mal = revisarClave(clave, repetida);
    if (mal) { setAviso(`clave.err.${mal}` as ClaveDeTexto); return; }
    if (!supabase) return;

    setOcupado(true);
    setAviso(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: clave });
      if (error) {
        // El texto de Supabase viene en inglés y con sus reglas. Lo que se
        // enseña es nuestro, y lo que se sabe seguro es que no se guardó.
        setAviso('clave.err.no_se_pudo');
        return;
      }
      setPaso({ que: 'listo' });
    } catch {
      setAviso('clave.err.sin_conexion');
    } finally {
      setOcupado(false);
    }
  }

  if (paso.que === 'canjeando') {
    return (
      <Marco>
        <p className="sub">{t('clave.checking')}</p>
        <div className="bar"><div className="bar__fill" style={{ width: '40%' }} /></div>
      </Marco>
    );
  }

  if (paso.que === 'listo') {
    return (
      <Marco>
        <p className="sub">{t('clave.done')}</p>
        <div className="alert alert--info"><span>{t('clave.doneBody')}</span></div>
        <p className="foot" style={{ marginTop: 'var(--s7)' }}>
          <a className="btn btn--primary btn--sm" href="/">{t('clave.enter')}</a>
        </p>
      </Marco>
    );
  }

  if (paso.que === 'sin_enlace' || paso.que === 'fallo') {
    const motivo = paso.que === 'fallo' ? paso.motivo : 'sin_enlace';
    return (
      <Marco>
        <p className="sub">{t('clave.linkTitle')}</p>
        <div className="alert alert--warn">
          <span>{t(`clave.${motivo}` as ClaveDeTexto)}</span>
        </div>
        <p className="foot" style={{ marginTop: 'var(--s7)' }}>
          <a className="btn btn--secondary btn--sm" href="/">{t('clave.back')}</a>
        </p>
      </Marco>
    );
  }

  return (
    <Marco>
      <p className="sub">{t(paso.invitado ? 'clave.subInvite' : 'clave.subRecovery')}</p>
      <form onSubmit={guardar} noValidate>
        <label className="f-dark">
          <span>{t('clave.new')}</span>
          <input
            type="password" autoComplete="new-password" required
            placeholder="••••••••••"
            value={clave} onChange={(e) => setClave(e.target.value)}
          />
        </label>
        <label className="f-dark">
          <span>{t('clave.repeat')}</span>
          <input
            type="password" autoComplete="new-password" required
            placeholder="••••••••••"
            value={repetida} onChange={(e) => setRepetida(e.target.value)}
          />
        </label>

        {aviso && (
          <p className="err" role="alert" style={{ color: 'var(--danger-border)' }}>
            {t(aviso)}
          </p>
        )}

        <button className="btn btn--primary btn--block" type="submit" disabled={ocupado}>
          {t('clave.save')}
        </button>
        <p className="foot">{t('clave.rule')}</p>
      </form>
    </Marco>
  );
}
